import { useMemo, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import type { ChatConversationSummary } from '@wundo/shared/chat.ts';
import {
  ChatSidebar as ChatSidebarColumn,
  ChatSidebarEmpty,
  ChatSidebarGroup,
  ChatSidebarItem,
  ChatSidebarList,
  ChatSidebarNew,
} from '@wundo/ui';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';
import { type ChatHistoryPage, fetchChatHistory } from '@/lib/chat.ts';
import { chatHistoryGroups } from './chatHistoryGroups.ts';

export interface ChatSidebarProps {
  /** Which conversation the drawer is showing, so the list can say so. */
  currentId: string | null;
  onOpen: (conversationId: string) => void;
  onNew: () => void;
}

/**
 * How close to the end counts as reaching it. The next page is asked for
 * before the reader arrives, so the list grows under them.
 */
const NEAR_END_PX = 160;

/**
 * The earlier conversations, beside the one being had.
 *
 * Full screen is the only place this exists — it is also the only place with
 * room for it — so this is where somebody goes to pick a conversation back up.
 * The list is the same data the drawer already reads; what is new is that it
 * stands open instead of replacing the conversation while you look.
 */
export function ChatSidebar({ currentId, onOpen, onNew }: ChatSidebarProps) {
  const { data, isError, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['chatHistory'],
      queryFn: async ({ pageParam }) => await fetchChatHistory(pageParam),
      initialPageParam: 1,
      getNextPageParam: (page: ChatHistoryPage) => page.older,
      /* A conversation carried on elsewhere reorders this list. Never held. */
      staleTime: 0,
    });

  const scroller = useRef<HTMLDivElement>(null);

  /* Pages run newest to oldest, which is the order the list is read in. */
  const groups = useMemo(
    () => chatHistoryGroups((data?.pages ?? []).flatMap((page) => page.conversations)),
    [data],
  );

  function onScroll() {
    const element = scroller.current;

    if (!element || !hasNextPage || isFetchingNextPage) {
      return;
    }

    const fromEnd = element.scrollHeight - element.scrollTop - element.clientHeight;

    if (fromEnd < NEAR_END_PX) {
      void fetchNextPage();
    }
  }

  const empty =
    !isPending && !isError && groups.thisWeek.length === 0 && groups.earlier.length === 0;

  return (
    <ChatSidebarColumn ref={scroller} onScroll={onScroll}>
      <ChatSidebarNew onClick={onNew}>
        <Plus aria-hidden='true' />
        {m.chat_sidebar_new()}
      </ChatSidebarNew>

      {isError ? (
        <p role='alert' className='px-2.5 py-5 text-sm text-danger'>
          {m.chat_history_failed()}
        </p>
      ) : null}

      {empty ? <ChatSidebarEmpty>{m.chat_history_empty()}</ChatSidebarEmpty> : null}

      <Group
        label={m.chat_history_this_week()}
        conversations={groups.thisWeek}
        currentId={currentId}
        onOpen={onOpen}
      />
      <Group
        label={m.chat_history_earlier()}
        conversations={groups.earlier}
        currentId={currentId}
        onOpen={onOpen}
      />

      {isFetchingNextPage ? (
        <p className='px-2.5 pt-3 text-xs text-ink-muted'>{m.chat_loading_older()}</p>
      ) : null}
    </ChatSidebarColumn>
  );
}

/** One heading and the conversations under it, or nothing where there are none. */
function Group({
  label,
  conversations,
  currentId,
  onOpen,
}: {
  label: string;
  conversations: ChatConversationSummary[];
  currentId: string | null;
  onOpen: (conversationId: string) => void;
}) {
  if (conversations.length === 0) return null;

  return (
    <>
      <ChatSidebarGroup>{label}</ChatSidebarGroup>
      <ChatSidebarList>
        {conversations.map((conversation) => (
          <ChatSidebarItem
            key={conversation.id}
            preview={conversation.preview}
            when={whenLabel(conversation)}
            current={conversation.id === currentId}
            onClick={() => onOpen(conversation.id)}
          />
        ))}
      </ChatSidebarList>
    </>
  );
}

/**
 * When a conversation was last spoken in: the time for today, the date for
 * anything older. A date on today's conversation reads as older than it is.
 */
function whenLabel(conversation: ChatConversationSummary): string {
  const at = new Date(conversation.last_message_at);
  const locale = getLocale();
  const isToday = new Date().toDateString() === at.toDateString();

  return isToday
    ? at.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    : at.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
}
